import { useState } from 'react';
import {
  EDP_MX_ID,
  PHILOSOPHIES,
  MICRO_INTERACTIONS,
  MICRO_INTERACTION_SPECS,
  AI_INTERACTIONS,
  FEEDBACK_TYPES,
  LOADING_PATTERNS,
  SUCCESS_MILESTONES,
  ERROR_FIELDS,
  BACKGROUND_JOBS,
  NOTIFICATION_CHANNELS,
  TRANSITIONS,
  EMOTIONAL_GOALS,
  MOTION_ACCESSIBILITY,
  ANALYTICS_METRICS,
  buildMotionDashboard,
} from '../../motionxp/framework';
import { FEEDBACK_DELAY_MS, MOTION_MAX_DURATION_MS } from '../../designsystem/tokens';
import {
  FeedbackToast,
  AIThinkingIndicator,
  ProgressFeedback,
  ErrorRecoveryCard,
  SuccessCelebration,
  MotionTokenPreview,
} from '../../components/motionxp';
import { Card, KpiCard, Skeleton } from '../../components/ui';

export function MotionExperiencePage() {
  const dashboard = buildMotionDashboard();
  const [progress, setProgress] = useState(62);
  const [showToast, setShowToast] = useState(true);

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{EDP_MX_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Motion & Emotional Experience Framework</h1>
        <p className="mt-1 text-sm text-slate-500">
          Micro-interactions, feedback architecture, AI interaction states, and emotional experience standards — intelligent, calm, responsive, trustworthy.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Philosophies" value={dashboard.philosophies.toString()} />
        <KpiCard label="Micro-Interactions" value={dashboard.micro_interactions.toString()} />
        <KpiCard label="AI Interactions" value={dashboard.ai_interactions.toString()} />
        <KpiCard label="Max Duration" value={`${dashboard.max_duration_ms}ms`} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Design Philosophy</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {PHILOSOPHIES.map((p) => (
            <Card key={p.id} padding="sm">
              <p className="font-medium">{p.label}</p>
              <p className="mt-1 text-xs text-slate-500">{p.description}</p>
            </Card>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Live Component Demos</h2>
          <div className="mt-4 space-y-4">
            {showToast && (
              <FeedbackToast
                kind="success"
                title="Catalog sync complete"
                message="847 products synchronized with Amazon"
                onDismiss={() => setShowToast(false)}
                action={{ label: 'View details', onClick: () => {} }}
              />
            )}
            <AIThinkingIndicator stage="Evaluating pricing elasticity across 12 SKUs" />
            <ProgressFeedback
              value={progress}
              label="Importing product catalog"
              eta="~45 seconds"
            />
            <input
              type="range"
              min={0}
              max={100}
              value={progress}
              onChange={(e) => setProgress(Number(e.target.value))}
              className="w-full"
              aria-label="Adjust progress demo"
            />
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Error Recovery (7 Mandatory Fields)</h2>
          <div className="mt-4">
            <ErrorRecoveryCard
              title="Publishing failed"
              explanation="Amazon rejected the listing update due to a missing required attribute."
              impact="This product is not visible to customers until the listing is corrected and republished."
              recovery="Add the 'Country of Origin' attribute in Catalog Operations, then retry publishing."
              correlationId="err-7f3a2b1c-2026-0801"
              onRetry={() => {}}
              onAIAssist={() => {}}
            />
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Success Celebration</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <SuccessCelebration
            title="First product published!"
            message="Your listing is now live on Amazon."
            level="high"
            nextAction={{ label: 'Create your next listing', onClick: () => {} }}
          />
          <SuccessCelebration
            title="Bulk price update complete"
            message="23 SKUs updated successfully."
            level="low"
          />
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Loading Patterns</h2>
          <Card className="mt-4" padding="sm">
            <p className="mb-3 text-xs text-slate-500">Skeleton screen (preferred over spinners)</p>
            <Skeleton lines={3} />
          </Card>
          <div className="mt-3 flex flex-wrap gap-2">
            {LOADING_PATTERNS.map((p) => (
              <span key={p} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{p}</span>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Motion Design Tokens</h2>
          <Card className="mt-4" padding="sm">
            <MotionTokenPreview />
          </Card>
          <p className="mt-2 text-xs text-slate-500">
            Feedback delay: {FEEDBACK_DELAY_MS}ms · Max duration: {MOTION_MAX_DURATION_MS}ms · Reduced motion via <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">prefers-reduced-motion</code>
          </p>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Micro-Interaction Timing</h2>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left dark:border-slate-700">
                <th className="pb-2 pr-4 font-medium">Interaction</th>
                <th className="pb-2 pr-4 font-medium">Duration</th>
                <th className="pb-2 pr-4 font-medium">Easing</th>
                <th className="pb-2 font-medium">Behavior</th>
              </tr>
            </thead>
            <tbody>
              {MICRO_INTERACTION_SPECS.map((s) => (
                <tr key={s.interaction} className="border-b border-slate-100 dark:border-slate-800">
                  <td className="py-2 pr-4 capitalize">{s.interaction.replace(/_/g, ' ')}</td>
                  <td className="py-2 pr-4 font-mono text-xs">{s.duration_ms}ms</td>
                  <td className="py-2 pr-4 font-mono text-xs">{s.easing}</td>
                  <td className="py-2 text-slate-500">{s.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {MICRO_INTERACTIONS.map((m) => (
            <span key={m} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{m}</span>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">AI Interactions</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {AI_INTERACTIONS.map((a) => (
              <span key={a} className="rounded-lg border border-ai-200 bg-ai-50 px-2 py-1 text-xs text-ai-700 dark:border-ai-800 dark:bg-ai-950 dark:text-ai-300">{a}</span>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">System Feedback Types</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {FEEDBACK_TYPES.map((f) => (
              <span key={f} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{f}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="text-lg font-semibold">Transitions</h2>
          <dl className="mt-3 space-y-1 text-sm">
            {TRANSITIONS.map((t) => (
              <div key={t.type} className="flex justify-between">
                <dt className="text-slate-600 dark:text-slate-400">{t.type}</dt>
                <dd className="font-mono text-xs">{t.duration_ms}ms</dd>
              </div>
            ))}
          </dl>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Emotional Goals</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {EMOTIONAL_GOALS.map((g) => (
              <span key={g} className="rounded-lg border border-brand-200 bg-brand-50 px-2 py-1 text-xs text-brand-700 dark:border-brand-800 dark:bg-brand-950 dark:text-brand-300">{g}</span>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Motion Accessibility</h2>
          <ul className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-400">
            {MOTION_ACCESSIBILITY.map((a) => (
              <li key={a}>✓ {a}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Background Processing</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {BACKGROUND_JOBS.map((j) => (
              <span key={j} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{j}</span>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Success Milestones</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {SUCCESS_MILESTONES.map((m) => (
              <span key={m} className="rounded-lg border border-success-200 bg-success-50 px-2 py-1 text-xs text-success-700 dark:border-success-800 dark:bg-success-950 dark:text-success-300">{m}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Notification Channels</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {NOTIFICATION_CHANNELS.map((n) => (
              <span key={n} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{n}</span>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Error Fields (Mandatory)</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {ERROR_FIELDS.map((f) => (
              <span key={f} className="rounded-lg border border-error-200 bg-error-50 px-2 py-1 text-xs text-error-700 dark:border-error-800 dark:bg-error-950 dark:text-error-300">{f}</span>
            ))}
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Measurement KPIs</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ANALYTICS_METRICS.map((m) => (
            <span key={m} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{m}</span>
          ))}
        </div>
      </section>

      <p className="text-xs text-slate-500">
        Spec: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">docs/edp/EDP-00008_MOTION_EMOTIONAL_EXPERIENCE.md</code>
        {' · '}
        Registry: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">pkg/motionxp/</code>
      </p>
    </div>
  );
}
